import type { Candidate, OnboardingStatus } from "../../../types/onboarding";
import type { WhitelistEntry } from "../../../types/whitelist";

export interface HrStepContext {
  candidate?: Candidate;
  whitelist?: WhitelistEntry | null;
  /** Sem a permissão rh.salary.view, a etapa Salário não conta no progresso. */
  canViewSalary?: boolean;
  isPj?: boolean;
}

/** Definição de uma etapa interna do processo de RH. */
export interface HrProcessStep {
  /** Identificador único da etapa. */
  key: string;
  /** Nome exibido ao usuário. */
  label: string;
  /**
   * Retorna true quando a etapa está concluída.
   * Usa os dados disponíveis no contexto — `candidate` para a tabela,
   * `detail`/`whitelist` para o drawer.
   */
  isCompleted: (ctx: HrStepContext) => boolean;
}

/**
 * Etapas internas do processo de RH, em ordem de exibição.
 *
 * Para adicionar uma nova etapa, basta inserir um objeto aqui.
 * O progresso da barra e do drawer são calculados automaticamente.
 */
export const HR_PROCESS_STEPS: HrProcessStep[] = [
  {
    key: "recruiter",
    label: "Recrutador responsável",
    isCompleted: ({ whitelist }) => !!(whitelist?.recruiter),
  },
  {
    key: "responsible_manager",
    label: "Gestor responsável",
    isCompleted: ({ whitelist }) => !!(whitelist?.responsible_manager),
  },
  {
    key: "job_title",
    label: "Cargo",
    isCompleted: ({ whitelist }) => !!(whitelist?.job_title),
  },
  {
    key: "job_activities",
    label: "Atividades do cargo",
    isCompleted: ({ whitelist }) => !!(whitelist?.job_activities),
  },
  {
    key: "salary",
    label: "Salário",
    isCompleted: ({ whitelist }) => !!whitelist?.salary && whitelist.salary !== "",
  },
  {
    key: "meal_voucher",
    label: "Vale refeição",
    isCompleted: ({ whitelist }) => !!whitelist?.meal_voucher && whitelist.meal_voucher !== "",
  },
  {
    key: "cost_center",
    label: "Centro de custo",
    isCompleted: ({ whitelist }) => !!(whitelist?.cost_center),
  },
  {
    key: "workplace",
    label: "Local de trabalho",
    isCompleted: ({ whitelist }) => !!(whitelist?.workplace),
  },
  {
    key: "work_schedule",
    label: "Escala",
    isCompleted: ({ whitelist }) => !!(whitelist?.work_schedule),
  },
  {
    key: "work_hours",
    label: "Horário de atuação",
    isCompleted: ({ whitelist }) => !!(whitelist?.work_start_time) && !!(whitelist?.work_end_time),
  },
  {
    key: "admission_date",
    label: "Data de admissão",
    isCompleted: ({ whitelist }) => !!(whitelist?.admission_date),
  },
  {
    key: "exam_convocation_attachment",
    label: "Anexo da guia de agendamento",
    isCompleted: ({ whitelist }) => !!(whitelist?.exam_convocation_attachment_name),
  },
  // Conta junto com o anexo (o envio da convocação sempre grava os dois flags),
  // mas mantém a contagem em sync com HrProcess.HR_TOTAL_STEPS (15) no backend.
  {
    key: "exam_email",
    label: "E-mail de convocação enviado",
    isCompleted: ({ whitelist }) => !!(whitelist?.exam_email_sent_at),
  },
  {
    key: "admission_exam",
    label: "Exame admissional (ASO)",
    isCompleted: ({ whitelist }) => !!(whitelist?.admission_exam_completed),
  },
  {
    key: "benefits_email",
    label: "E-mail de benefícios",
    isCompleted: ({ whitelist }) => !!(whitelist?.benefits_email_sent),
  },
];

/** Chaves das etapas de exame admissional — não se aplicam a candidatos PJ. */
export const HR_EXAM_STEP_KEYS = ["exam_convocation_attachment", "admission_exam"];

/**
 * Retorna as etapas aplicáveis ao contexto — remove as de exame admissional
 * para PJ (não têm ASO/exame) e a de Salário sem a permissão rh.salary.view.
 */
export function getApplicableSteps(ctx: HrStepContext): HrProcessStep[] {
  const steps = ctx.isPj
    ? HR_PROCESS_STEPS.filter(s => !HR_EXAM_STEP_KEYS.includes(s.key))
    : HR_PROCESS_STEPS;
  return ctx.canViewSalary === false
    ? steps.filter(s => s.key !== "salary")
    : steps;
}

/**
 * Retorna quantas etapas estão concluídas e o total,
 * para uso na barra de progresso.
 */
export function getHrProgress(ctx: HrStepContext): {
  completed: number;
  total: number;
  percent: number;
} {
  const steps = getApplicableSteps(ctx);
  const completed = steps.filter(s => s.isCompleted(ctx)).length;
  const total = steps.length;
  return { completed, total, percent: total === 0 ? 0 : Math.round((completed / total) * 100) };
}

/**
 * Status do processo interno de RH derivado do progresso das etapas:
 *  - 0 etapas preenchidas        → AGUARDANDO_RH
 *  - 1 até total-1 preenchidas    → EM_ANDAMENTO
 *  - todas preenchidas            → CONCLUIDO
 */
export function getHrStatus(whitelist: WhitelistEntry | null, canViewSalary = true, isPj = false): OnboardingStatus {
  const { completed, total } = getHrProgress({ whitelist, canViewSalary, isPj });
  if (total === 0 || completed === 0) return "AGUARDANDO_RH";
  if (completed >= total) return "CONCLUIDO";
  return "EM_ANDAMENTO";
}
